<template lang="pug">
div(class="submit-mc-container")

    //- Header
    div(class="form-header")
        div(class="header-icon-wrap")
            b-icon(icon="file-medical" font-scale="1.8")
        div
            h6(class="form-title") Submit MC / Document
            small(class="text-muted") Upload proof for sessions you cannot attend

    div.my-2(class="alert alert-warning" role="alert")
        b-icon(icon="exclamation-triangle-fill" class="mr-2")
        small
            strong Note:
            //- span &nbsp;Only regular caregivers can submit MC here.
            br
            span 1. Clear image showing the date of MC/HL/Doctor's appointment
            br
            span 2. Name of client or name of a regular family caregiver who usually accompanies client to the Centre
            br
            //- span(class="text-muted mt-2 d-block")
            //-     b Please note:&nbsp;
            //-     | Your MC submission is now pending for approval

            span(class="text-danger mt-2 d-block")
                | If failed to follow No: 1 or 2 the MC submission will be rejected.

    div(class="form-body")
        b-form(@submit.prevent="submitMC")

            //- Date Selection
            div(class="form-section" data-tour="mc-dates")
                div(class="section-label")
                    b-icon(icon="calendar3" class="mr-1")
                    | MC Dates
                    span(class="required-mark" title="Required") *

                //- Mode toggle: continuous range vs specific days
                div(class="mode-toggle mt-2 mb-2")
                    button(
                        type="button"
                        class="mode-btn"
                        :class="{'mode-btn--active': mcMode === 'range'}"
                        @click="setMode('range')"
                    ) Date range
                    button(
                        type="button"
                        class="mode-btn"
                        :class="{'mode-btn--active': mcMode === 'selective'}"
                        @click="setMode('selective')"
                    ) Specific days

                small(class="text-muted d-block mb-3")
                    template(v-if="mcMode === 'range'") Select the start and end dates of your MC leave
                    template(v-else) Tap each individual day you'll be absent (e.g. Mon &amp; Thu)

                div(class="calendar-wrap")
                    date-picker(
                        v-model="mcDates"
                        type="date"
                        :range="mcMode === 'range'"
                        :multiple="mcMode === 'selective'"
                        inline
                        :disabled-date="disabledDate"
                        :lang="calendarLang"
                        range-separator=" – "
                        :placeholder="mcMode === 'range' ? 'Select date range' : 'Select days'"
                        class="mc-calendar"
                    )

                transition(name="slide-up")
                    div(v-if="selectedCount" class="selection-pill mt-3")
                        b-icon(icon="calendar-check" class="mr-1")
                        | {{ selectionLabel }}

            //- Request Type
            div(class="form-section")
                div(class="section-label")
                    b-icon(icon="tag" class="mr-1")
                    | Request Type
                div(class="type-pill-group mt-2")
                    label(
                        v-for="opt in typeOptions"
                        :key="opt.value"
                        class="type-pill"
                        :class="{'type-pill--active': requestType === opt.value}"
                        @click="requestType = opt.value"
                    )
                        input(type="radio" :value="opt.value" v-model="requestType" class="sr-only")
                        b-icon(:icon="opt.value === 1 ? 'file-medical' : 'file-earmark-text'" class="mr-1")
                        | {{ opt.text }}

            //- Reason
            div(class="form-section" data-tour="mc-reason")
                div(class="section-label")
                    b-icon(icon="chat-left-text" class="mr-1")
                    | Reason
                    span(class="required-mark" title="Required") *
                    small(class="text-muted ml-2" style="font-weight:400") min. 10 characters
                b-form-textarea(
                    v-model="reason"
                    placeholder="Describe why you cannot attend…"
                    rows="3"
                    class="reason-textarea mt-2"
                    :class="reason.length >= 10 ? 'border-success' : ''"
                )

            //- File Upload
            div(class="form-section" data-tour="mc-proof")
                div(class="section-label")
                    b-icon(icon="paperclip" class="mr-1")
                    | Document(s)
                    span(class="required-mark" title="Required") *
                    small(class="text-muted ml-2" style="font-weight:400") PDF · JPG · PNG · up to 5 files · 4 MB in total

                div(class="upload-zone mt-2")
                    b-form-file(
                        id="document"
                        v-model="documentInput"
                        accept=".pdf,.jpg,.jpeg,.png"
                        placeholder="Tap to choose files…"
                        drop-placeholder="Drop here…"
                        :state="fileValidation"
                        multiple
                        @change="onFilesAdded"
                        class="upload-input"
                    )

                div(v-if="optimising" class="text-muted small mt-2")
                    b-spinner(small class="mr-1")
                    | Optimising photo…
                div(v-if="documents.length" class="file-list mt-2")
                    div(
                        v-for="(doc, index) in documents"
                        :key="index"
                        class="file-item"
                    )
                        div(class="file-icon-wrap")
                            b-icon(icon="file-earmark-text" variant="primary")
                        div(class="file-info")
                            span(class="file-name") {{ doc.name }}
                            span(class="file-size") {{ formatFileSize(doc.size) }}
                                template(v-if="doc.originalSize")  (reduced from {{ formatFileSize(doc.originalSize) }})
                        b-button(size="sm" variant="link" class="file-remove p-0" @click="removeFile(index)")
                            b-icon(icon="x-circle-fill" variant="danger")

                b-form-invalid-feedback(:state="fileValidation" class="mt-1")
                    | {{ fileError }}

            //- CTA
            div(class="cta-bar")
                b-button(data-tour="mc-submit" 
                    type="submit"
                    variant="primary"
                    :disabled="!canSubmit || submitting"
                    class="w-100 cta-btn"
                )
                    b-spinner(small v-if="submitting" class="mr-2")
                    span(v-if="submitting") Submitting…
                    span(v-else)
                        b-icon(icon="cloud-arrow-up-fill" class="mr-2")
                        | Submit Request

    //- Success Modal
    b-modal(v-model="showSuccessModal" title="" hide-footer centered)
        div(class="text-center py-4 px-3")
            lottie-animation(:animation-data="successAnimation" :autoPlay="true" :loop="false" :speed="1" class="w-50 mx-auto")
            h5(class="mt-2 fw-700") Request Submitted!
            p(class="text-muted small") Your MC document has been uploaded — it's listed under "My MC Requests".
            b-button(variant="primary" class="px-5 mt-1" @click="resetForm") Done

    //- Caregiver Email Modal — approval-outcome emails are sent to the caregiver
    //- who submitted the MC, so an email address is required before submitting.
    //- Saving updates the caregiver profile, then the submission continues.
    b-modal(v-model="showEmailModal" title="Email Required" hide-footer centered no-close-on-backdrop)
        div(class="py-2 px-1")
            p(class="small text-muted")
                | We'll email you when this request is approved or rejected, but there's no
                | email address on your caregiver profile yet. Please add one to continue.
            b-form-group(label="Your Email Address" label-class="small fw-600")
                b-form-input(
                    v-model="caregiverEmail"
                    type="email"
                    placeholder="name@example.com"
                    :state="caregiverEmailValid"
                    :disabled="savingEmail"
                )
                b-form-invalid-feedback Please enter a valid email address
            .d-flex.justify-content-end.mt-3
                b-button.mr-2(variant="outline-secondary" @click="showEmailModal = false" :disabled="savingEmail") Cancel
                b-button(variant="primary" @click="saveCaregiverEmail" :disabled="savingEmail || caregiverEmailValid !== true")
                    b-spinner(small v-if="savingEmail")
                    span(v-else) Save & Continue
</template>

<script>
import dayjs from 'dayjs';
import errorModal from '@/mixins/errorModal';
import LottieAnimation from 'lottie-web-vue';
import successAnimation from '@/assets/success.json';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
    mixins: [errorModal],
    components: {
        LottieAnimation,
        DatePicker
    },
    props: {
        userId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            successAnimation,
            mcMode: 'range',   // 'range' = contiguous start–end, 'selective' = specific days
            mcDates: [],       // range mode: [start, end]; selective mode: [d1, d2, ...]
            requestType: 1,    // 1 = MC, 2 = Other
            reason: '',
            documentInput: null,
            documents: [],
            optimising: false,
            submitting: false,
            showSuccessModal: false,
            showEmailModal: false,
            caregiverEmail: '',
            savingEmail: false,
            swappedDates: new Set(), // YYYY-MM-DD strings for active swap original/replacement dates
            mcCoveredDates: new Set(), // YYYY-MM-DD strings covered by active (pending/approved) MC requests
            typeOptions: [
                { text: 'Medical Certificate (MC)', value: 1 },
                { text: 'Other Supporting Document', value: 2 }
            ],
            calendarLang: {
                formatLocale: { firstDayOfWeek: 1 },
                monthBeforeYear: false
            }
        };
    },
    computed: {
        fileValidation() {
            if (!this.documents.length) return null;
            if (this.documents.length > 5) return false;
            // 4 MB in TOTAL across all files (API Gateway -> Lambda base64 payload limit is 6 MB)
            if (this.documents.reduce((t, d) => t + (d.size || 0), 0) > 4 * 1024 * 1024) return false;
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            for (const doc of this.documents) {
                if (!allowedTypes.includes(doc.type)) return false;
            }
            return true;
        },
        fileError() {
            if (!this.documents.length) return '';
            if (this.documents.length > 5) return 'You can attach up to 5 files';
            const total = this.documents.reduce((t, d) => t + (d.size || 0), 0);
            if (total > 4 * 1024 * 1024) return `All files together must be under 4 MB (currently ${(total / 1024 / 1024).toFixed(1)} MB) — try a smaller photo`;
            const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
            for (const doc of this.documents) {
                if (!allowedTypes.includes(doc.type)) return `${doc.name}: Only PDF, JPG, and PNG files are allowed`;
            }
            return '';
        },
        // Canonical list of YYYY-MM-DD strings, regardless of mode.
        mcDateList() {
            const arr = (this.mcDates || []).filter(Boolean);
            if (this.mcMode === 'range') {
                if (arr.length !== 2) return [];
                const start = dayjs(arr[0]).startOf('day');
                const end = dayjs(arr[1]).startOf('day');
                const n = end.diff(start, 'day');
                if (n < 0) return [];
                return Array.from({ length: n + 1 }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
            }
            return arr.map(d => dayjs(d).format('YYYY-MM-DD'));
        },
        selectedCount() {
            return this.mcDateList.length;
        },
        selectionLabel() {
            const n = this.selectedCount;
            if (!n) return '';
            if (this.mcMode === 'range') {
                return `${this.formatDate(this.mcDates[0])} – ${this.formatDate(this.mcDates[1])} (${n} day${n > 1 ? 's' : ''})`;
            }
            return `${n} day${n > 1 ? 's' : ''} selected`;
        },
        canSubmit() {
            return (
                this.selectedCount > 0 &&
                this.selectedCount <= 60 &&
                this.requestType &&
                this.reason.length >= 10 &&
                this.documents.length > 0 &&
                this.fileValidation === true
            );
        },

        caregiverEmailValid() {
            if (!this.caregiverEmail) return null;
            return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.caregiverEmail.trim());
        }
    },
    mounted() {
        this.loadAvailableDates();
    },
    methods: {
        setMode(mode) {
            if (this.mcMode === mode) return;
            this.mcMode = mode;
            this.mcDates = []; // selection shape differs between modes — reset on switch
        },
        disabledDate(date) {
            // MC may cover the existing (current) month or upcoming dates — floor is the
            // start of the current month (independent of the swap lead-time rule).
            const minDate = dayjs().startOf('month').startOf('day').toDate();
            if (date < minDate) return true;
            // Dates already covered by an active MC/Other request, or used in an
            // active session swap, can't be picked again — greyed out up front.
            const key = dayjs(date).format('YYYY-MM-DD');
            return this.swappedDates.has(key) || this.mcCoveredDates.has(key);
        },
        async loadAvailableDates() {
            try {
                const { data } = await this.$root.axios.get('/makeup_sessions/existing-makeups', {
                    params: { userID: this.userId }
                });
                const makeups = data?.makeups || [];
                const dates = new Set();
                const covered = new Set();
                makeups
                    .filter(m => m.crb5c_requeststatus !== 2)
                    .forEach(m => {
                        if (m.crb5c_requesttype === 0) {
                            if (m.crb5c_originaldate) dates.add(dayjs(m.crb5c_originaldate).format('YYYY-MM-DD'));
                            if (m.crb5c_replacementdate) dates.add(dayjs(m.crb5c_replacementdate).format('YYYY-MM-DD'));
                            return;
                        }
                        // Pending or approved MC/Other: every date it covers
                        let meta = null;
                        try { meta = m.crb5c_mcdaterange ? JSON.parse(m.crb5c_mcdaterange) : null; } catch (e) { meta = null; }
                        if (meta && Array.isArray(meta.dates) && meta.dates.length) {
                            meta.dates.forEach(d => covered.add(d));
                        } else if (meta && meta.start && meta.end) {
                            let cur = dayjs(meta.start); const end = dayjs(meta.end);
                            while (!cur.isAfter(end, 'day')) { covered.add(cur.format('YYYY-MM-DD')); cur = cur.add(1, 'day'); }
                        } else if (m.crb5c_originaldate) {
                            covered.add(dayjs(m.crb5c_originaldate).format('YYYY-MM-DD'));
                        }
                    });
                this.swappedDates = dates;
                this.mcCoveredDates = covered;
            } catch (e) {
                console.error('Failed to load swapped dates:', e);
            }
        },
        // Approval-outcome emails go to the submitting caregiver, so an email
        // address must exist on their profile before the MC can be submitted.
        // Returns true when ok to proceed; otherwise opens the email modal.
        async ensureCaregiverEmail() {
            try {
                const { data } = await this.$root.axios.get('/makeup_sessions/caregiver-info', {
                    params: { userID: this.userId }
                });
                if (data?.caregiver && !data.caregiver.hasEmail) {
                    this.showEmailModal = true;
                    return false;
                }
            } catch (e) {
                // Pre-check is best-effort — the backend enforces it on submit too.
                console.error('Caregiver info check failed:', e);
            }
            return true;
        },

        async saveCaregiverEmail() {
            if (this.caregiverEmailValid !== true) return;
            this.savingEmail = true;
            try {
                await this.$root.axios.post('/makeup_sessions/update-caregiver-email', {
                    userID: this.userId,
                    email: this.caregiverEmail.trim()
                });
                this.showEmailModal = false;
                this.showErrorModal('Error', 'Email saved to your profile', {
                    title: 'Saved', variant: 'success', solid: true
                });
                // Continue the submission the modal interrupted.
                this.submitMC();
            } catch (error) {
                this.showErrorModal('Email not saved', error.response?.data?.error || 'Failed to save email. Please try again.');
            } finally {
                this.savingEmail = false;
            }
        },

        async submitMC() {
            if (!this.canSubmit) return;

            if (!(await this.ensureCaregiverEmail())) return;

            this.submitting = true;

            try {
                const clientName = this.$store?.state?.cli_name || this.$root.currentUser[0]?.crb5c_client?.crb5c_name || 'Client';
                const dates = this.mcDateList; // canonical YYYY-MM-DD list (deduped/sorted below)
                const uniqueSorted = [...new Set(dates)].sort();

                // Capped at 60 days (range or selective) — matches backend validation
                if (uniqueSorted.length > 60) {
                    this.showErrorModal('Too Many Days', 'MC cannot exceed 60 days. Please select fewer days.');
                    this.submitting = false;
                    return;
                }

                // Canonical payload: dates[] is the source of truth; range also carries start/end.
                const payload = this.mcMode === 'range'
                    ? { mode: 'range', start: uniqueSorted[0], end: uniqueSorted[uniqueSorted.length - 1], dates: uniqueSorted }
                    : { mode: 'selective', dates: uniqueSorted };
                const mcDateRangeJson = JSON.stringify(payload);

                const formData = new FormData();
                formData.append('userID', this.userId);
                formData.append('requestType', this.requestType);
                formData.append('mcDateRange', mcDateRangeJson);
                formData.append('reason', this.reason);
                formData.append('clientName', clientName);
                this.documents.forEach(doc => formData.append('documents', doc));
                
                const { data } = await this.$root.axios.post('/makeup_sessions/create', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                
                if (data && data.message === 'created') {
                    this.showSuccessModal = true;
                    this.$emit('request-submitted');
                }
            } catch (error) {
                console.error('Error submitting MC:', error);
                // Backend enforces the caregiver-email requirement too — open the
                // email modal instead of showing a raw error.
                if (error.response?.data?.requiresCaregiverEmail) {
                    this.showEmailModal = true;
                } else {
                    const status = error.response?.status;
                    const serverMsg = error.response?.data?.error || error.response?.data?.message;
                    let msg;
                    if (status === 413) msg = 'The files are too large to upload. Please use smaller photos (or fewer files).';
                    else if (status === 504 || status === 502) msg = 'The upload took too long. Please try again on a stronger connection or with smaller photos.';
                    else if (serverMsg) msg = serverMsg;
                    else if (!error.response) msg = `Could not reach the server (${error.message || 'network error'}). Check your connection and try again.`;
                    else msg = `Failed to submit request (error ${status}). Please try again.`;
                    this.showErrorModal('Error', msg);
                }
            } finally {
                this.submitting = false;
            }
        },
        resetForm() {
            this.mcMode = 'range';
            this.mcDates = [];
            this.requestType = 1;
            this.reason = '';
            this.documentInput = null;
            this.documents = [];
            this.showSuccessModal = false;
            this.loadAvailableDates();
        },
        async onFilesAdded(event) {
            const newFiles = Array.from(event.target?.files || []);
            this.$nextTick(() => { this.documentInput = null; });
            this.optimising = true;
            try {
                for (const f of newFiles) {
                    if (this.documents.length >= 5) break;
                    if (this.documents.some(d => d.name === f.name && d.size === f.size)) continue;
                    // Phone photos are 3–5 MB; shrink them so uploads are fast and stay
                    // well under the 4 MB request cap. PDFs are left untouched.
                    const optimised = await this.compressImage(f);
                    this.documents.push(optimised);
                }
            } finally {
                this.optimising = false;
            }
        },
        // Resize an image to max 1600px on its long side and re-encode as JPEG 0.82.
        // An MC at 1600px is still ~190 dpi on A4 — text and stamps stay legible.
        // Returns the original file when it's small already, not an image, or if
        // decoding fails (older browsers / unusual formats).
        async compressImage(file) {
            const isImage = /^image\/(jpeg|jpg|png)$/i.test(file.type) || /\.(jpe?g|png)$/i.test(file.name);
            if (!isImage || file.size <= 700 * 1024) return file;
            try {
                const bitmap = await this.loadBitmap(file);
                const MAX = 1600;
                const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
                const w = Math.round(bitmap.width * scale);
                const h = Math.round(bitmap.height * scale);
                const canvas = document.createElement('canvas');
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
                if (bitmap.close) bitmap.close();
                const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.82));
                if (!blob || blob.size >= file.size) return file;
                const name = file.name.replace(/\.(jpe?g|png)$/i, '') + '.jpg';
                const out = new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
                out.originalSize = file.size;
                return out;
            } catch (e) {
                console.warn('Image compression skipped:', e?.message);
                return file;
            }
        },
        loadBitmap(file) {
            if (window.createImageBitmap) {
                return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(() => this.loadViaImg(file));
            }
            return this.loadViaImg(file);
        },
        loadViaImg(file) {
            return new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
                img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
                img.src = url;
            });
        },
        removeFile(index) {
            this.documents.splice(index, 1);
        },
        formatDate(date) {
            return dayjs(date).format('ddd, MMM D, YYYY');
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
        }
    }
};
</script>

<style lang="scss" scoped>
$purple: #8967B3;
$radius-lg: 16px;
$radius-md: 12px;
$radius-sm: 8px;
$shadow-sm: 0 2px 8px rgba(0,0,0,0.08);
$transition: all 0.22s ease;

.submit-mc-container {
    min-height: 55vh;
    background: #f7f6fb;
    border-radius: $radius-lg;
    overflow: hidden;
}

// Header
.form-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.25rem;
    background: white;
    border-bottom: 1px solid #ede9f4;
}
.header-icon-wrap {
    width: 44px;
    height: 44px;
    background: #ede9f4;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $purple;
    flex-shrink: 0;
}
.form-title {
    font-weight: 700;
    margin: 0;
    color: #222;
}

// Body
.form-body { padding: 1rem; }

// Form sections
.form-section {
    background: white;
    border-radius: $radius-md;
    padding: 1rem;
    margin-bottom: 0.75rem;
    box-shadow: $shadow-sm;
}
.section-label {
    font-size: 0.82rem;
    font-weight: 700;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.required-mark {
    color: #dc3545;
    font-weight: 800;
    margin-left: 0.2rem;
}

// Calendar range picker
.calendar-wrap {
    display: flex;
    justify-content: center;
}
.mc-calendar {
    width: 100%;
    // Override vue2-datepicker colours to match app purple theme
    :deep(.mx-calendar-header button),
    :deep(.mx-btn) {
        color: $purple;
        font-weight: 600;
    }
    :deep(.mx-table-date .cell.today) {
        color: $purple;
        font-weight: 700;
    }
    :deep(.mx-table-date .cell.in-range) {
        background: #f0eaf8;
        color: $purple;
    }
    :deep(.mx-table-date .cell.active),
    :deep(.mx-table-date .cell.active:hover) {
        background: $purple;
        color: white;
        border-radius: 50%;
    }
    :deep(.mx-table-date .cell:not(.disabled):hover) {
        background: #e8dff5;
        border-radius: 50%;
    }
    :deep(.mx-table-date .cell.disabled) {
        color: #ddd;
        background: none;
        cursor: not-allowed;
        pointer-events: none;
    }
    :deep(.mx-datepicker-inline) {
        width: 100%;
        box-shadow: none;
        border: none;
    }
    // Single-panel layout: hide the second calendar panel so only one month shows at a time.
    // Range selection still works; user navigates to the end-date month and clicks.
    :deep(.mx-range-wrapper) {
        display: block;
    }
    :deep(.mx-range-wrapper .mx-calendar:last-child) {
        display: none;
    }
    // Make the single visible calendar fill the container
    :deep(.mx-range-wrapper .mx-calendar:first-child) {
        width: 100%;
        min-width: 0;
    }
    :deep(.mx-calendar) {
        width: 100%;
        padding: 0.5rem 0.25rem;
    }
    :deep(.mx-calendar-content) {
        height: auto;
    }
    :deep(.mx-table th),
    :deep(.mx-table-date .cell) {
        font-size: 0.82rem;
    }
}
.selection-pill {
    display: inline-flex;
    align-items: center;
    background: #e8f5e9;
    color: #2e7d32;
    font-size: 0.78rem;
    font-weight: 600;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
}

// Mode toggle (range vs specific days)
.mode-toggle { display: inline-flex; gap: 0; border: 1.5px solid #e0d9f0; border-radius: 20px; overflow: hidden; }
.mode-btn {
    border: none;
    background: white;
    color: #888;
    font-size: 0.78rem;
    font-weight: 600;
    padding: 0.3rem 0.9rem;
    cursor: pointer;
    transition: $transition;
    &:focus { outline: none; }
    &--active { background: $purple; color: white; }
}

// Request type pills
.type-pill-group { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.type-pill {
    flex: 1;
    min-width: 120px;
    padding: 0.55rem 0.75rem;
    border-radius: 24px;
    border: 1.5px solid #e0d9f0;
    background: white;
    color: #888;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    text-align: center;
    transition: $transition;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    &--active { background: $purple; border-color: $purple; color: white; }
    &:hover:not(.type-pill--active) { border-color: $purple; color: $purple; }
}

// Textarea
.reason-textarea {
    border-radius: $radius-sm;
    font-size: 0.85rem;
    &:focus { border-color: $purple; box-shadow: 0 0 0 0.2rem rgba(137,103,179,0.2); }
}

// Upload zone
.upload-zone {
    border: 2px dashed #d0c8e8;
    border-radius: $radius-sm;
    padding: 0.75rem;
    background: #faf8ff;
    transition: $transition;
    &:hover { border-color: $purple; background: #f5effb; }
}
.upload-input { font-size: 0.82rem; }

// File list
.file-list { display: flex; flex-direction: column; gap: 0.4rem; }
.file-item {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.75rem;
    background: #f7f6fb;
    border-radius: $radius-sm;
    border: 1px solid #e8e4f0;
}
.file-icon-wrap { flex-shrink: 0; }
.file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.file-name { font-size: 0.8rem; font-weight: 600; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.file-size { font-size: 0.68rem; color: #999; }
.file-remove { flex-shrink: 0; }

// CTA
.cta-bar { margin-top: 0.5rem; }
.cta-btn { border-radius: 24px; font-weight: 600; font-size: 0.95rem; padding: 0.65rem; }

// Utilities
.fw-700 { font-weight: 700; }

// Slide-up transition
.slide-up-enter-active, .slide-up-leave-active { transition: all 0.25s ease; }
.slide-up-enter, .slide-up-leave-to { opacity: 0; transform: translateY(6px); }
</style>
